import { useCallback, useEffect, useMemo, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import {
  API_KEY_ACCESS,
  API_KEY_EXPIRY,
  API_KEY_MODULES,
  buildCreatePayload,
  canManageApiKeys,
  scopeLabel,
  statusBadge,
  validateApiKeyForm,
} from '../../lib/apiKeys.js'
import '../financeiro/contas.css'
import './meu-perfil.css'
import './config-usuarios.css'
import './config-api.css'

function fmtDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('pt-BR')
}

const EMPTY_FORM = {
  name: '',
  access: { crm: 'read' },
  target: 'client',
  clientId: '',
  expiresInDays: '365',
}

function CreateModal({ open, creating, form, setForm, clients, onClose, onCreate }) {
  if (!open) return null
  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }))

  return (
    <div className="fc-modal-overlay" onMouseDown={creating ? undefined : onClose}>
      <div className="fc-modal" style={{ width: 640 }} role="dialog" aria-modal="true" aria-labelledby="api-create-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="fc-modal-head" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 id="api-create-title">Nova chave de API</h2>
            <div className="fc-desc-sub">Use o menor acesso possível. A chave age em seu nome.</div>
          </div>
          <button type="button" className="fc-icon-btn" onClick={onClose} disabled={creating} title="Fechar">
            <Icon n="x" s={15} />
          </button>
        </div>
        <div className="fc-modal-body">
          <label className="us-select-field api-field"><span>Nome da chave</span>
            <input
              className="fc-ctrl"
              value={form.name}
              maxLength={120}
              placeholder="Ex.: agente de atendimento"
              onChange={(event) => patch({ name: event.target.value })}
              disabled={creating}
            />
          </label>

          <div className="us-invite-fields">
            <label className="us-select-field"><span>Alcance</span>
              <select className="fc-ctrl" value={form.target} onChange={(event) => patch({ target: event.target.value })} disabled={creating}>
                <option value="client">Um cliente</option>
                <option value="organization">Todos os clientes da organização</option>
              </select>
            </label>
            <label className="us-select-field"><span>Cliente</span>
              <select
                className="fc-ctrl"
                value={form.clientId}
                onChange={(event) => patch({ clientId: event.target.value })}
                disabled={creating || form.target !== 'client'}
              >
                <option value="">Selecione...</option>
                {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
              </select>
            </label>
          </div>

          <div className="us-select-field api-field"><span>Acesso por módulo</span>
            <div className="fc-card">
              <table className="fc-table">
                <tbody>
                  {API_KEY_MODULES.map((mod) => (
                    <tr key={mod.key}>
                      <td>{mod.label}</td>
                      <td>
                        <select
                          className="fc-ctrl"
                          aria-label={`Acesso a ${mod.label}`}
                          value={form.access[mod.key] || ''}
                          onChange={(event) => patch({ access: { ...form.access, [mod.key]: event.target.value } })}
                          disabled={creating}
                        >
                          {API_KEY_ACCESS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <label className="us-select-field api-field"><span>Validade</span>
            <select className="fc-ctrl" value={form.expiresInDays} onChange={(event) => patch({ expiresInDays: event.target.value })} disabled={creating}>
              {API_KEY_EXPIRY.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        </div>
        <div className="fc-modal-foot">
          <button type="button" className="fc-ctrl" onClick={onClose} disabled={creating}>Cancelar</button>
          <button type="button" className="fc-ctrl primary" onClick={onCreate} disabled={creating}>
            {creating ? 'Criando...' : 'Criar chave'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Mostra o segredo uma única vez. Fechar descarta o valor da memória.
function SecretModal({ secret, onCopy, onClose }) {
  if (!secret) return null
  return (
    <div className="fc-modal-overlay">
      <div className="fc-modal" style={{ width: 640 }} role="dialog" aria-modal="true" aria-labelledby="api-secret-title">
        <div className="fc-modal-head">
          <div>
            <h2 id="api-secret-title">Chave criada</h2>
            <div className="fc-desc-sub">{secret.name}</div>
          </div>
        </div>
        <div className="fc-modal-body">
          <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>
            Copie e guarde esta chave agora. Ela não será exibida novamente.
          </div>
          <code className="api-secret">{secret.key}</code>
        </div>
        <div className="fc-modal-foot">
          <button type="button" className="fc-ctrl" onClick={onCopy}>Copiar chave</button>
          <button type="button" className="fc-ctrl primary" onClick={onClose}>Já guardei</button>
        </div>
      </div>
    </div>
  )
}

export default function ConfigApi() {
  const { toast, memberships, availableClients, activeOrganizationId } = useApp()
  const allowed = canManageApiKeys(memberships || [])
  const clients = useMemo(() => (availableClients || []).filter((c) => c.is_active !== false), [availableClients])
  const clientName = useMemo(() => Object.fromEntries((availableClients || []).map((c) => [c.id, c.name])), [availableClients])

  const [keys, setKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [secret, setSecret] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await api.listApiKeys()
      setKeys(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message || 'Não foi possível carregar as chaves.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (allowed) load()
    else setLoading(false)
  }, [allowed, load])

  function openCreate() {
    setForm({ ...EMPTY_FORM, clientId: clients.length === 1 ? clients[0].id : '' })
    setError('')
    setCreateOpen(true)
  }

  async function handleCreate() {
    const problem = validateApiKeyForm(form)
    if (problem) { toast(problem, 'error'); return }
    setCreating(true)
    try {
      const created = await api.createApiKey(buildCreatePayload({ ...form, organizationId: activeOrganizationId }))
      setCreateOpen(false)
      setSecret({ name: created.name, key: created.key })
      await load()
    } catch (err) {
      toast(err.status === 403 ? 'Só owner ou admin da organização cria chaves de API.' : (err.message || 'Falha ao criar a chave.'), 'error')
    } finally {
      setCreating(false)
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(secret.key)
      toast('Chave copiada.', 'success')
    } catch {
      toast('Não foi possível copiar. Selecione e copie manualmente.', 'error')
    }
  }

  async function handleRevoke(item) {
    if (!window.confirm(`Revogar a chave "${item.name}"? Quem a usa perde o acesso na hora.`)) return
    try {
      await api.revokeApiKey(item.id)
      toast('Chave revogada.', 'success')
      await load()
    } catch (err) {
      toast(err.message || 'Falha ao revogar a chave.', 'error')
    }
  }

  if (!allowed) {
    return (
      <div className="mp-page us-page">
        <div className="mp-card"><div className="empty">
          <div className="empty-icon"><Icon n="key" s={18} /></div>
          <div className="empty-title">Acesso restrito</div>
          <div className="empty-desc">Só owner ou admin da organização gerencia chaves de API.</div>
        </div></div>
      </div>
    )
  }

  return (
    <div className="mp-page us-page">
      <header className="mp-card us-page-header">
        <div className="us-heading">
          <span className="us-heading-icon"><Icon n="key" s={21} /></span>
          <div><h1>Chaves de API</h1><p>Dê a integrações, scripts e agentes acesso aos dados do CRM sem usar o seu login.</p></div>
        </div>
        <button type="button" className="mp-button mp-button-primary" onClick={openCreate}><Icon n="plus" s={14} />Nova chave</button>
      </header>

      <div className="mp-card us-workspace">
        <div className="us-tab-content">
          {error && <div className="auth-error" role="alert" style={{ margin: '16px 0' }}>{error}</div>}
          {loading ? (
            <div className="fc-card"><div className="empty"><div className="empty-desc">Carregando chaves...</div></div></div>
          ) : !keys.length ? (
            <div className="fc-card"><div className="empty">
              <div className="empty-icon"><Icon n="key" s={18} /></div>
              <div className="empty-title">Nenhuma chave criada</div>
              <div className="empty-desc">Crie uma chave para conectar um agente ou integração.</div>
            </div></div>
          ) : (
            <div className="fc-card" style={{ marginTop: 16 }}>
              <div className="fc-table-scroll">
                <table className="fc-table" style={{ minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th>Nome</th><th>Chave</th><th>Acesso</th><th>Alcance</th>
                      <th>Status</th><th>Expira em</th><th>Último uso</th>
                      <th className="fc-col-acoes">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((item) => {
                      const [tone, label] = statusBadge(item.status)
                      return (
                        <tr key={item.id}>
                          <td><div className="fc-desc">{item.name}</div></td>
                          <td><code>{item.prefix}</code></td>
                          <td>{(item.scopes || []).map(scopeLabel).join(', ')}</td>
                          <td>{item.client_id ? (clientName[item.client_id] || 'Um cliente') : 'Toda a organização'}</td>
                          <td><span className={`badge badge-${tone}`}>{label}</span></td>
                          <td>{fmtDate(item.expires_at)}</td>
                          <td>{item.last_used_at ? fmtDate(item.last_used_at) : 'Nunca'}</td>
                          <td className="fc-col-acoes">
                            {item.status === 'ativa' && (
                              <button type="button" className="fc-icon-btn danger" title="Revogar" aria-label={`Revogar ${item.name}`} onClick={() => handleRevoke(item)}>
                                <Icon n="x" s={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateModal
        open={createOpen}
        creating={creating}
        form={form}
        setForm={setForm}
        clients={clients}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />
      <SecretModal secret={secret} onCopy={handleCopy} onClose={() => setSecret(null)} />
    </div>
  )
}
